/**
 * Shared formatting helpers (doc 03 §3.2, doc 06 §9, doc 09 §8).
 * All data numerals get tabular-nums via the UI layer; these produce the
 * strings. Relative times always expose an absolute `<time datetime>` in the
 * UI layer — here we return both forms.
 */
const MIN = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;
/**
 * A count, grouped.
 *
 * "48213" is four digits a reader has to count before they know whether it is
 * forty-eight thousand or four hundred and eighty-two thousand; "48,213" is
 * read at a glance. The screens this matters most on — the Compliance Center's
 * evidence figures, the audit category roll-up, analytics totals — were all
 * rendering `String(n)` straight from the payload.
 *
 * `en-US` explicitly rather than the runtime's locale: these strings are
 * compared against each other down a column and read beside copy the product
 * ships only in English, so a browser set to de-DE producing "48.213" in one
 * place and English copy in the next is a worse outcome than a consistent
 * separator. Revisit with the rest of localization, not before.
 *
 * Not for identifiers. A record number, a year or a code is not a quantity and
 * must not be grouped.
 */
export function formatCount(n) {
    if (!Number.isFinite(n))
        return '—';
    return n.toLocaleString('en-US');
}
/** Human relative time + ISO absolute (doc 09 §8: expose absolute to AT/hover). */
export function relativeTime(then, now = new Date()) {
    const diff = now.getTime() - then.getTime();
    const abs = then.toISOString();
    if (diff < MIN)
        return { short: 'just now', absolute: abs };
    if (diff < HOUR) {
        const m = Math.floor(diff / MIN);
        return { short: `${m}m ago`, absolute: abs };
    }
    if (diff < DAY) {
        const h = Math.floor(diff / HOUR);
        return { short: `${h}h ago`, absolute: abs };
    }
    const d = Math.floor(diff / DAY);
    if (d === 1)
        return { short: 'yesterday', absolute: abs };
    if (d < 7)
        return { short: `${d}d ago`, absolute: abs };
    return { short: then.toLocaleDateString(), absolute: abs };
}
/** Format a duration (ms) as `H:MM` or `M:SS`. */
export function formatDuration(ms) {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}` : `${m}:${pad(s)}`;
}
/** Format a clock time as `H:MM AM/PM`. */
export function formatClock(then) {
    return then.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
/**
 * Doc 03 §3.5 / doc 04 §20: render occurred+received separately only when they
 * differ beyond 60s; otherwise a single time. This is the decision helper —
 * the component renders the two or one values.
 */
export function timestampsDiffer(ts, thresholdMs = 60_000) {
    return Math.abs(ts.receivedAt.getTime() - ts.occurredAt.getTime()) > thresholdMs;
}
//# sourceMappingURL=format.js.map